import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WarningCircle } from '@phosphor-icons/react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  checkDraftForm,
  estimateReelSeconds,
  type DraftDto,
  type DraftLookInput,
  type PublicationKind,
  type RenderDraftStateDto,
} from '@app/shared';
import { ExamplePlayer } from '../components/ExamplePlayer';
import { HelpHint } from '../components/HelpHint';
import { LookPicker } from '../components/LookPicker';
import { LookSummary } from '../components/LookSummary';
import { StepAction, StepLayout } from '../components/shell/StepLayout';
import { TextBlocksEditor } from '../components/TextBlocksEditor';
import { BottomSheet, Button, Card, Skeleton, SkeletonGroup, Spinner, Toast } from '../components/ui';
import { useToast } from '../hooks/useToast';
import { useT } from '../i18n';
import { api, ApiError } from '../lib/api';
import { AUTOSAVE_DELAY_MS, createAutosave, type AutosaveStatus } from '../lib/autosave';
import { answerOf } from '../lib/build-panel';
import { canBuyNow } from '../lib/payment-options';
import { busyInterval } from '../lib/busy';
import { editorReducer, emptyEditor, previewBlocks, toBlocksInput, type EditorAction } from '../lib/draft-editor';
import { errorText } from '../lib/errors';
import { countOf } from '../lib/format';
import {
  draftKind,
  exampleCaption,
  exampleOf,
  initialLook,
  lookAfterKind,
  mainAction,
  newerDraft,
  styleToPin,
  type Look,
} from '../lib/format-text';
import { stepBack } from '../lib/layout';
import { renderApi } from '../lib/render-client';
import { blockFromQuery } from '../lib/render-format';
import { haptic } from '../lib/tma';
import { affordLine, totalLine } from '../lib/voice-screen';

type Sheet = null | { kind: 'rewrite'; to: PublicationKind } | { kind: 'flags'; then: 'next' | 'build' };
type Note = { text: string; link: { to: string; label: string } | null };

/**
 * «Формат и текст» (spec 4): one example in the chosen format and style, the
 * format and style chips, then the text written for that format, edited right in
 * its blocks and saved by itself. One big button: «Написать текст», then «Дальше
 * — озвучка» for a reel or «Собрать» for a carousel. Nothing paid starts by
 * itself; another format after the text is a rewrite, asked first and limited.
 */
export function DraftPage() {
  const t = useT();
  const f = t.formatText;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast, show } = useToast();
  const id = Number(useParams().id);
  const valid = Number.isInteger(id) && id > 0;
  const key = useMemo(() => ['draft', id], [id]);
  const stateKey = useMemo(() => ['render-draft', id], [id]);

  const query = useQuery<DraftDto>({
    queryKey: key,
    queryFn: () => api.getDraft(id),
    enabled: valid,
    refetchInterval: (q) => busyInterval('draft', q.state.data?.status),
  });
  const draft = query.data;
  const stateQuery = useQuery<RenderDraftStateDto>({
    queryKey: stateKey,
    queryFn: () => renderApi.getDraftState(id),
    enabled: valid,
  });
  const state = stateQuery.data;

  const [look, setLook] = useState<Look | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [editor, dispatch] = useReducer(editorReducer, emptyEditor);
  const [acting, setActing] = useState<string | null>(null);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [saveStatus, setSaveStatus] = useState<AutosaveStatus>('idle');

  // The autosave reads the fields and the version they show through refs: it outlives renders.
  const editorRef = useRef(editor);
  editorRef.current = editor;
  /** The version the fields show; null until they are loaded. */
  const editorVersion = useRef<number | null>(null);
  /** The server's text replaces the fields, unsaved edits included (a conflict). */
  const forceReload = useRef(false);
  /** The draft's format as the server last said it: the look starts again only when it changes there. */
  const serverKind = useRef<PublicationKind | null>(null);
  const showRef = useRef(show);
  showRef.current = show;

  /** Replies arrive in any order: the cached draft only moves forward (a save and a style tap at once). */
  const keepNewer = (dto: DraftDto) => qc.setQueryData<DraftDto>(['draft', id], (old) => newerDraft(old, dto));

  /** The server's text replaces the fields now: a conflict must not wait for a new data object. */
  const reloadFromServer = async () => {
    try {
      const fresh = await api.getDraft(id);
      qc.setQueryData(['draft', id], fresh);
      if (fresh.version > 0 && fresh.status !== 'writing' && fresh.status !== 'editing') {
        dispatch({ type: 'load', blocks: fresh.blocks });
        editorVersion.current = fresh.version;
        forceReload.current = false;
      } else {
        forceReload.current = true;
      }
    } catch {
      forceReload.current = true;
      void qc.invalidateQueries({ queryKey: ['draft', id] });
    }
  };
  const reloadRef = useRef(reloadFromServer);
  reloadRef.current = reloadFromServer;

  const autosave = useMemo(
    () =>
      createAutosave({
        delayMs: AUTOSAVE_DELAY_MS,
        save: async () => {
          const base = editorVersion.current;
          if (base === null) return 'failed';
          try {
            const dto = await api.saveBlocks(id, { baseVersion: base, blocks: toBlocksInput(editorRef.current) });
            editorVersion.current = dto.version;
            qc.setQueryData<DraftDto>(['draft', id], (old) => newerDraft(old, dto));
            void qc.invalidateQueries({ queryKey: ['drafts'] });
            return 'saved';
          } catch (err) {
            if (err instanceof ApiError && err.code === 'VERSION_CONFLICT') {
              await reloadRef.current();
              showRef.current(t.formatText.conflict, 'warning');
              return 'conflict';
            }
            return 'failed';
          }
        },
      }),
    [id, qc, t],
  );
  useEffect(() => autosave.subscribe(setSaveStatus), [autosave]);
  // Leaving the screen, hiding or closing the app saves what is pending.
  useEffect(() => {
    const hidden = () => {
      if (document.visibilityState === 'hidden') void autosave.flush();
    };
    const leave = () => void autosave.flush();
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', leave);
    return () => {
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', leave);
      void autosave.flush();
    };
  }, [autosave]);

  // The fields follow the server only when it changed the text (an AI edit, undo, a rewrite, a conflict).
  useEffect(() => {
    if (!draft || draft.version === 0 || draft.status === 'writing' || draft.status === 'editing') return;
    const pendingEdits = autosave.status() === 'pending' || autosave.status() === 'saving';
    if (!forceReload.current && (editorVersion.current === draft.version || pendingEdits)) return;
    dispatch({ type: 'load', blocks: draft.blocks });
    editorVersion.current = draft.version;
    forceReload.current = false;
  }, [draft, autosave]);

  useEffect(() => {
    if (!draft || !state) return;
    const kind = draftKind(draft);
    if (look === null || kind !== serverKind.current) {
      serverKind.current = kind;
      if (look === null || look.kind !== kind) setLook(initialLook(state, draft));
    }
  }, [draft, state, look]);

  // Stage 3: a build refused at a block opens the text on it (?block=b2).
  const [params] = useSearchParams();
  const focusBlock = draft ? blockFromQuery(params.get('block'), draft.blocks.map((b) => b.id)) : null;

  const { pathname } = useLocation();
  const backTo = stepBack(pathname, { thoughtId: draft?.thoughtId });
  // Telegram «назад» never asks: nothing unsaved survives the flush.
  const guard = () => {
    void autosave.flush();
    return true;
  };

  const latest = (): DraftDto => qc.getQueryData<DraftDto>(key) ?? draft!;
  const keep = (dto: DraftDto) => {
    keepNewer(dto);
    void qc.invalidateQueries({ queryKey: ['drafts'] });
  };
  /** Waits for the autosave; false when the text on the screen could not be saved. */
  const saveFirst = async () => {
    const s = await autosave.flush();
    return s !== 'conflict' && s !== 'failed';
  };

  /** One request; its error is said and the draft refetched when it is stale. Resolves null on an error. */
  const run = async <T,>(name: string, call: () => Promise<T>): Promise<T | null> => {
    setActing(name);
    try {
      return await call();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN';
      const stale = code === 'VERSION_CONFLICT' || code === 'BUSY';
      if (code === 'VERSION_CONFLICT') void reloadFromServer();
      if (stale || code === 'FORM_ISSUES' || code === 'REWRITE_LIMIT' || code === 'USE_REWRITE') {
        void qc.invalidateQueries({ queryKey: key });
        void qc.invalidateQueries({ queryKey: stateKey });
      }
      show(errorText(err, t), stale ? 'warning' : 'destructive');
      return null;
    } finally {
      setActing(null);
    }
  };

  /** An action on the saved text: the autosave goes first, then the call on the fresh version. */
  const textAction = async (name: string, call: (version: number) => Promise<DraftDto>) => {
    if (!(await saveFirst())) return;
    const dto = await run(name, () => call(latest().version));
    if (dto) keep(dto);
  };

  const storeLook = async (next: Look, body: DraftLookInput) => {
    const prev = look;
    setLook(next);
    try {
      keep(await api.setDraftLook(id, body));
      void qc.invalidateQueries({ queryKey: stateKey });
    } catch (err) {
      setLook(prev);
      show(errorText(err, t), 'destructive');
    }
  };

  const onKind = (kind: PublicationKind) => {
    if (!draft || !state || !look) return;
    if (draft.version > 0) setSheet({ kind: 'rewrite', to: kind });
    else {
      const next = lookAfterKind(state, look, kind);
      void storeLook(next, { kind, style: next.style });
    }
  };

  const rewrite = async (kind: PublicationKind) => {
    setSheet(null);
    if (!state || !look || !(await saveFirst())) return;
    const next = lookAfterKind(state, look, kind);
    const dto = await run('rewrite', () => api.writeDraft(id, { kind, style: next.style, baseVersion: latest().version }));
    if (!dto) return;
    setLook(next);
    setShowPicker(false);
    keep(dto);
    void qc.invalidateQueries({ queryKey: stateKey });
  };

  const write = async () => {
    if (!look) return;
    const dto = await run('write', () => api.writeDraft(id, { kind: look.kind, style: look.style }));
    if (dto) keep(dto);
  };

  /** Saves, asks about open notes, approves (no separate «Утвердить», spec 4.6); false when it stopped. */
  const approveFirst = async (then: 'next' | 'build', skipNotes: boolean): Promise<boolean> => {
    if (!(await saveFirst())) return false;
    const d = latest();
    if (!skipNotes && d.flags.some((x) => x.state === 'open')) {
      setSheet({ kind: 'flags', then });
      return false;
    }
    setSheet(null);
    // The build uses the stored style: store the one on screen first (a default would follow the last build).
    const pin = state && look ? styleToPin(state, look) : null;
    if (pin !== null) {
      const pinned = await run('approve', () => api.setDraftLook(id, { style: pin }));
      if (!pinned) return false;
      keep(pinned);
      void qc.invalidateQueries({ queryKey: stateKey });
    }
    if (d.status === 'approved' && d.approvedVersion === d.version) return true;
    const dto = await run('approve', () => api.approveDraft(id, d.version));
    if (!dto) return false;
    keep(dto);
    return true;
  };

  const goNext = async (skipNotes = false) => {
    if (!(await approveFirst('next', skipNotes))) return;
    haptic('success');
    navigate(`/drafts/${id}/voice`);
  };

  const build = async (skipNotes = false) => {
    if (!look?.style || !(await approveFirst('build', skipNotes))) return;
    setNote(null);
    setActing('build');
    try {
      const res = await renderApi.startBuild({ draftId: id, style: look.style, animated: look.animated, music: 'none' });
      const a = answerOf(res, id, t, { canBuy: res.kind === 'no_credits' && (await canBuyNow(qc)) });
      if (a.kind === 'navigate') navigate(a.to);
      else if (a.kind === 'note') setNote({ text: a.text, link: a.link });
      else void qc.invalidateQueries({ queryKey: stateKey });
    } catch {
      setNote({ text: t.common.failed, link: null });
    } finally {
      setActing(null);
    }
  };

  const onEdit = (action: EditorAction) => {
    dispatch(action);
    autosave.change();
  };

  const page = (content: JSX.Element) => (
    <StepLayout title={f.screenTitle} back={backTo} guard={guard} carousel={draft?.format === 'carousel'}>
      {content}
      {toast}
    </StepLayout>
  );

  if (query.isError || stateQuery.isError) {
    return page(
      <Card variant="surface" padding="lg" className="text-center">
        <WarningCircle size={24} className="mx-auto mb-2 text-destructive" />
        <p className="text-sm break-words">{errorText(query.error ?? stateQuery.error, t)}</p>
        <Link to="/" className="inline-block mt-4 text-sm font-semibold text-fg">
          {t.common.home}
        </Link>
      </Card>,
    );
  }

  // The blocks are coming: bones in their shape (spec 4.3).
  const blockBones = (
    <SkeletonGroup label={t.common.loading} className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-24" />
      ))}
    </SkeletonGroup>
  );
  if (!draft || !state || !look) {
    return page(
      <>
        <Skeleton className="w-[55%] mx-auto aspect-[9/16] mb-4" />
        {blockBones}
      </>,
    );
  }

  const busy = draft.status === 'writing' || draft.status === 'editing';
  const hasText = draft.version > 0;
  const collapsed = hasText && !showPicker;
  const preview = previewBlocks(editor, draft.format);
  const textOnScreen = hasText && draft.status !== 'writing' && draft.status !== 'failed';
  const issues = textOnScreen ? checkDraftForm(draft.format, preview, draft.limits) : [];
  const estimate =
    textOnScreen && draft.format === 'reel' ? estimateReelSeconds(preview, draft.limits.reel.wordsPerSecond) : null;
  const action = mainAction(draft, look, state, { issues: issues.length, pending: acting !== null }, t);
  const carouselQuote = look.animated ? state.quote.animated : state.quote.static;
  const openNotes = draft.flags.filter((x) => x.state === 'open').length;

  const onMain = () => {
    haptic('medium');
    if (action.action === 'write') void write();
    else if (action.action === 'retry') void run('retry', () => api.retryDraft(id)).then((dto) => dto && keep(dto));
    else if (action.action === 'next') void goNext();
    else if (action.action === 'build') void build();
  };

  return page(
    <>
      {collapsed ? (
        <LookSummary state={state} look={look} disabled={busy} onChange={() => setShowPicker(true)} />
      ) : (
        <>
          <ExamplePlayer example={exampleOf(state, look)} caption={exampleCaption(state, look, t)} />
          <LookPicker
            state={state}
            draft={draft}
            look={look}
            disabled={busy || acting !== null}
            onKind={onKind}
            onStyle={(style) => void storeLook({ ...look, style }, { style })}
            onAnimated={(animated) => void storeLook({ ...look, animated }, { animated })}
          />
        </>
      )}

      {draft.status === 'writing' ? (
        <>
          <p className="flex items-center gap-2 text-sm text-hint mb-3">
            <Spinner size="sm" />
            {f.writing}
          </p>
          {blockBones}
        </>
      ) : draft.status === 'failed' ? (
        <Card variant="surface" padding="lg" className="text-center mb-4">
          <WarningCircle size={24} className="mx-auto mb-2 text-destructive" />
          <p className="text-sm">{f.failed}</p>
        </Card>
      ) : textOnScreen ? (
        <TextBlocksEditor
          draft={draft}
          editor={editor}
          issues={issues}
          estimate={estimate}
          readOnly={draft.status === 'editing' || acting !== null}
          acting={acting}
          saveStatus={saveStatus}
          focusBlock={focusBlock}
          onEdit={onEdit}
          onBlur={() => void autosave.blur()}
          onFlag={(flagId, a) =>
            void textAction(`${a}:${flagId}`, (v) => (a === 'keep' ? api.keepFlag(id, flagId, v) : api.removeFlag(id, flagId, v)))
          }
          onSuggestion={(sid, a) =>
            void textAction(`${a}:${sid}`, (v) => (a === 'add' ? api.addSuggestion(id, sid, v) : api.dismissSuggestion(id, sid, v)))
          }
          onUndo={() => void textAction('undo', (v) => api.undoDraft(id, v))}
        />
      ) : null}

      {note && (
        <Toast tone="warning" className="mb-4">
          {note.text}
          {note.link && (
            <>
              {' '}
              <Link to={note.link.to} className="underline font-medium">
                {note.link.label}
              </Link>
            </>
          )}
        </Toast>
      )}

      <StepAction>
        {action.action === 'build' && (
          <div className="mb-2">
            <p className="flex items-center gap-1.5 text-sm font-semibold">
              {totalLine(carouselQuote, state.freeRebuilds, t)}
              <HelpHint topic="credits" />
            </p>
            <p className="text-xs text-hint">
              {affordLine(state.balance, carouselQuote.free ? 0 : carouselQuote.price, 'carousel', t)}
            </p>
          </div>
        )}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!action.enabled}
          loading={acting === action.action || (action.action === 'next' && acting === 'approve')}
          onClick={onMain}
        >
          {action.label}
        </Button>
      </StepAction>

      <BottomSheet open={sheet?.kind === 'rewrite'} onClose={() => setSheet(null)} title={f.rewriteTitle}>
        {sheet?.kind === 'rewrite' && (
          <>
            <p className="text-sm text-hint mb-4">
              {f.rewriteText(f.formats[sheet.to], state.rewritesLeft, state.rewritesLimit)}
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setSheet(null)}>
                {f.cancel}
              </Button>
              <Button variant="ink" fullWidth onClick={() => void rewrite(sheet.to)}>
                {f.rewrite}
              </Button>
            </div>
          </>
        )}
      </BottomSheet>

      <BottomSheet open={sheet?.kind === 'flags'} onClose={() => setSheet(null)}>
        {sheet?.kind === 'flags' && (
          <>
            <p className="text-sm mb-4">{f.flagsLeft(openNotes, countOf(openNotes, t.units.notes))}</p>
            <div className="flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setSheet(null)}>
                {f.cancel}
              </Button>
              <Button
                variant="ink"
                fullWidth
                onClick={() => void (sheet.then === 'next' ? goNext(true) : build(true))}
              >
                {f.goOn}
              </Button>
            </div>
          </>
        )}
      </BottomSheet>
    </>,
  );
}
